import { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SitStatDragHandle } from 'components/MapPage/CorporateViewTabs/SitStatTab/SitStatDragHandle';
import { ActionAttachmentsSection } from 'components/IAP/actionTicket/ActionAttachmentsSection';
import type { ActionAttachment } from 'components/IAP/actionTicket/actionTicketModel';
import { isVersionedForm } from 'components/IAP/femaCards/formSectionSchema';
import {
  useFormVersionTip,
  useSetFormVersionAttachments,
} from 'components/IAP/hooks/icsFormVersionHooks';
import { STEPPER_FORM_WIDGETS } from './stepperFormStatus';
import type { CardOption } from './cardOptions';
import {
  FormsDropdown,
  RenderWidget,
  type WidgetRenderContext,
} from './widgetRegistry';

const MAX_PANES = 3;

/** Wide forms make closest-center stay on the form you grabbed. Compare the pointer to each pane's horizontal center instead. */
const horizontalPointerCollision: CollisionDetection = ({
  droppableContainers,
  droppableRects,
  pointerCoordinates,
}) => {
  if (!pointerCoordinates) return [];
  let best: { id: (typeof droppableContainers)[number]['id']; distance: number } | null =
    null;
  for (const container of droppableContainers) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    const distance = Math.abs(pointerCoordinates.x - (rect.left + rect.width / 2));
    if (!best || distance < best.distance) {
      best = { id: container.id, distance };
    }
  }
  return best ? [{ id: best.id }] : [];
};

type Pane = { id: string; widget: string };

function newPane(widget: string): Pane {
  return {
    id: `pane-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    widget,
  };
}

function FormAttachmentStrip({
  baseId,
  incidentId,
  periodNumber,
}: {
  baseId: string;
  incidentId: string;
  periodNumber: number;
}) {
  const tip = useFormVersionTip(baseId, periodNumber);
  const save = useSetFormVersionAttachments(baseId, periodNumber);
  const [rows, setRows] = useState<ActionAttachment[]>([]);
  const savedKey = JSON.stringify(tip.data?.attachments ?? []);

  useEffect(() => {
    setRows(tip.data?.attachments ?? []);
  }, [tip.data?.id, savedKey]);

  return (
    <div className="dark tw:rounded-lg tw:bg-background tw:text-foreground">
      <ActionAttachmentsSection
        incidentId={incidentId}
        value={rows}
        onChange={(next) => {
          setRows(next);
          save.mutate(next);
        }}
      />
    </div>
  );
}

function SortablePane({
  pane,
  options,
  reorderable,
  tourAnchor,
  ctx,
  incidentId,
  onChange,
  onClose,
}: {
  pane: Pane;
  options: CardOption[];
  reorderable: boolean;
  tourAnchor: boolean;
  ctx: WidgetRenderContext;
  incidentId: string;
  onChange: (widget: string) => void;
  onClose: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pane.id });
  const baseId = STEPPER_FORM_WIDGETS[pane.widget]?.baseId;
  const title =
    options.find((option) => option.value === pane.widget)?.label ??
    pane.widget;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={`tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2 ${isDragging ? 'tw:opacity-40' : ''}`}
      {...(tourAnchor ? { 'data-tour': 'planning-form-card' } : {})}
    >
      <div className="tw:flex tw:items-center tw:gap-1">
        {reorderable ? (
          <div className="dark tw:inline-flex tw:rounded-md tw:bg-background tw:text-foreground">
            <SitStatDragHandle
              ariaLabel={`Reorder ${title}`}
              isDragging={isDragging}
              setActivatorNodeRef={setActivatorNodeRef}
              attributes={attributes}
              listeners={listeners}
            />
          </div>
        ) : null}
        <FormsDropdown
          id={`form-pane-${pane.id}`}
          options={options}
          value={pane.widget}
          onChange={onChange}
        />
        {reorderable ? (
          <div className="dark tw:inline-flex tw:rounded-md tw:bg-background tw:text-foreground">
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              aria-label={`Close ${title}`}
              onClick={onClose}
            >
              <X />
            </Button>
          </div>
        ) : null}
      </div>
      <RenderWidget widgetName={pane.widget} ctx={ctx} />
      {baseId && isVersionedForm(baseId) ? (
        <FormAttachmentStrip
          baseId={baseId}
          incidentId={incidentId}
          periodNumber={ctx.renderPeriodNumber}
        />
      ) : null}
    </div>
  );
}

export function FormPaneRow({
  options,
  initialWidget,
  ctx,
  incidentId,
  onPrimaryChange,
}: {
  options: CardOption[];
  initialWidget: string;
  ctx: WidgetRenderContext;
  incidentId: string;
  onPrimaryChange?: (widget: string) => void;
}) {
  const [panes, setPanes] = useState<Pane[]>(() =>
    initialWidget ? [newPane(initialWidget)] : []
  );
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  useEffect(() => {
    if (!initialWidget) return;
    setPanes((current) => {
      const first = current[0]?.widget ?? null;
      const initialListed = options.some((option) => option.value === initialWidget);
      if (current.length === 0) return [newPane(initialWidget)];
      if (first === initialWidget || !initialListed) return current;
      const existing = current.findIndex((pane) => pane.widget === initialWidget);
      if (existing > 0) {
        return [
          current[existing],
          ...current.filter((_, index) => index !== existing),
        ];
      }
      return current.map((pane, index) =>
        index === 0 ? { ...pane, widget: initialWidget } : pane
      );
    });
  }, [initialWidget, options]);

  const addPane = () => {
    setPanes((current) => {
      if (current.length >= MAX_PANES) return current;
      const unused =
        options.find(
          (option) => !current.some((pane) => pane.widget === option.value)
        ) ?? options[0];
      if (!unused) return current;
      const next = [...current, newPane(unused.value)];
      return next;
    });
  };

  const onDragStart = (event: DragEndEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const onDragCancel = () => {
    setActiveDragId(null);
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPanes((current) => {
      const fromIndex = current.findIndex((pane) => pane.id === active.id);
      const toIndex = current.findIndex((pane) => pane.id === over.id);
      if (fromIndex < 0 || toIndex < 0) return current;
      const next = arrayMove(current, fromIndex, toIndex);
      const primary = next[0]?.widget;
      if (primary) onPrimaryChange?.(primary);
      return next;
    });
  };

  if (options.length === 0) {
    return (
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        No ICS forms for this seat.
      </p>
    );
  }

  const reorderable = panes.length > 1;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:justify-end">
        <div className="dark tw:inline-flex tw:rounded-md tw:bg-background tw:text-foreground">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={panes.length >= MAX_PANES || options.length === 0}
            onClick={addPane}
          >
            <Plus /> Add form
          </Button>
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={horizontalPointerCollision}
        onDragStart={onDragStart}
        onDragCancel={onDragCancel}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={panes.map((pane) => pane.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="tw:flex tw:items-start tw:gap-3">
            {panes.map((pane, index) => (
              <SortablePane
                key={pane.id}
                pane={pane}
                options={options}
                reorderable={reorderable}
                tourAnchor={index === 0}
                ctx={ctx}
                incidentId={incidentId}
                onChange={(widget) =>
                  setPanes((current) => {
                    const next = current.map((row) =>
                      row.id === pane.id ? { ...row, widget } : row
                    );
                    const primary = next[0]?.widget;
                    if (primary) onPrimaryChange?.(primary);
                    return next;
                  })
                }
                onClose={() =>
                  setPanes((current) => {
                    const next = current.filter((row) => row.id !== pane.id);
                    const primary = next[0]?.widget;
                    if (primary) onPrimaryChange?.(primary);
                    return next;
                  })
                }
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeDragId ? (
            <div className="dark tw:rounded-md tw:border tw:border-border tw:bg-background tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:text-foreground tw:shadow-lg">
              Moving{' '}
              {options.find(
                (option) =>
                  option.value ===
                  panes.find((pane) => pane.id === activeDragId)?.widget
              )?.label ?? 'form'}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
